import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: 120px 0;
  background: var(--gradient-surface);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 1200px;
    height: 1200px;
    border-radius: 50%;
    background: var(--gradient-primary);
    top: -600px;
    left: -300px;
    opacity: 0.05;
    animation: float 8s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-30px) rotate(5deg); }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const Content = styled.div`
  order: 2;
  max-width: 480px;
  animation: slideIn var(--transition-normal) forwards;
  
  h2 {
    font-size: 42px;
    font-weight: 800;
    margin-bottom: 24px;
    line-height: 1.2;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: 18px;
    color: var(--text-secondary);
    margin-bottom: 20px;
    line-height: 1.6;
  }

  @media (max-width: 968px) {
    order: 1;
    text-align: center;
    margin: 0 auto;
  }
`;

const MockupContainer = styled.div`
  order: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  animation: fadeIn var(--transition-normal) forwards;
`;

const IPadFrame = styled.div`
  background: var(--surface);
  border-radius: 40px;
  padding: 20px;
  box-shadow: var(--shadow-lg);
  width: 700px;
  max-width: 100%;
  position: relative;
  transition: all var(--transition-normal);
  border: 1px solid var(--border);

  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-primary);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    border-radius: 4px 4px 0 0;
  }
`;

const Screen = styled.div`
  background: var(--background);
  border-radius: 30px;
  overflow: hidden;
  height: 400px;
  display: flex;
  gap: 16px;
  padding: 16px;
  transition: all var(--transition-normal);
`;

const NotificationCard = styled.div`
  background: var(--surface);
  padding: 20px;
  border-radius: 16px;
  box-shadow: var(--shadow-sm);
  width: 45%;
  height: fit-content;
  transition: all var(--transition-normal);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--border);
  padding-bottom: 16px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);

  sup {
    background: var(--primary);
    color: white;
    padding: 2px 6px;
    border-radius: 8px;
    font-size: 10px;
    margin-left: 4px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
  font-size: 14px;
  
  span {
    cursor: pointer;
    transition: all var(--transition-normal);
    
    &:first-child {
      color: var(--text-secondary);
      &:hover { color: var(--primary); }
    }
    
    &:last-child {
      color: var(--error);
      &:hover { opacity: 0.8; }
    }
  }
`;

const NotificationItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px;
  border-radius: 12px;
  transition: all var(--transition-normal);

  &:hover {
    background: var(--surface-hover);
  }
`;

const Icon = styled.div`
  background: var(--primary-light);
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition: all var(--transition-normal);
`;

const Message = styled.div`
  flex: 1;
  
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: var(--text-primary);

    &.time {
      color: var(--text-secondary);
      font-size: 12px;
      margin-top: 4px;
    }
    
    &.view {
      color: var(--primary);
      font-size: 13px;
      margin-top: 8px;
      cursor: pointer;
      transition: all var(--transition-normal);

      &:hover {
        color: var(--primary-dark);
      }
    }
  }
`;

const EmailCard = styled.div`
  background: var(--surface);
  padding: 20px;
  border-radius: 16px;
  box-shadow: var(--shadow-sm);
  width: 55%;
  height: fit-content;
  transition: all var(--transition-normal);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
  }

  .email-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-bottom: 1px solid var(--border);
  }

  .email-icon {
    background: var(--primary);
    color: white;
    padding: 6px 10px;
    border-radius: 8px;
    font-size: 12px;
  }

  .email-title {
    font-size: 14px;
    color: var(--text-secondary);
  }

  .email-content {
    background: var(--background);
    padding: 24px;
    border-radius: 12px;
    margin: 16px 0;
  }

  .email-body {
    h3 {
      font-size: 20px;
      margin-bottom: 16px;
      color: var(--text-primary);
      font-weight: 600;
    }

    p {
      font-size: 14px;
      color: var(--text-secondary);
      margin-bottom: 12px;
      line-height: 1.6;
    }

    .subscription-details {
      margin: 20px 0;
      padding: 16px;
      background: var(--surface);
      border-radius: 12px;
      border: 1px solid var(--border);
      
      p {
        margin: 8px 0;
        color: var(--text-primary);
        font-size: 14px;
      }
    }

    .button {
      display: inline-block;
      background: var(--gradient-primary);
      color: white;
      padding: 10px 20px;
      border-radius: 8px;
      font-size: 14px;
      margin: 20px 0;
      text-decoration: none;
      transition: all var(--transition-normal);

      &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-primary);
      }
    }
  }

  .footer {
    font-size: 12px;
    color: var(--text-secondary);
    text-align: center;
    margin-top: 16px;
  }
`;

const SmartReminders = () => {
  return (
    <Section>
      <Container>
        <MockupContainer>
          <IPadFrame>
            <Screen>
              <NotificationCard>
                <NotificationHeader>
                  <Title>Notifications<sup>1</sup></Title>
                  <Actions>
                    <span>Mark all as read</span>
                    <span>Clear</span>
                  </Actions>
                </NotificationHeader>
                <NotificationItem>
                  <Icon>⏰</Icon>
                  <Message>
                    <p>Payment Due Date Reminder</p>
                    <p className="time">1 minute ago</p>
                    <p>Your payment for 'iCloud' is due tomorrow.</p>
                    <p className="view">👁 View</p>
                  </Message>
                </NotificationItem>
              </NotificationCard>
              
              <EmailCard>
                <div className="email-header">
                  <span className="email-icon">🔔</span>
                  <span className="email-title">Subscriptions Reminder</span>
                </div>
                <div className="email-content">
                  <div className="email-body">
                    <h3>Nexpend</h3>
                    <p>Dear John,</p>
                    <p>Here is a list of your subscriptions due tomorrow:</p>
                  
                    <div className="subscription-details">
                      <p>Subscription: iCloud</p>
                      <p>Provider: Apple</p>
                      <p>Amount: $9.00</p>
                    </div>
                    <p>View Subscriptions</p>

                    //<a href="#" className="button">View Subscriptions</a>

                    <p>Thank you for using Nexpend!</p>
                    <p>Regards,<br/>Nexpend</p>
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
                  © 2024 Nexpend. All rights reserved.
                </div>
              </EmailCard>
            </Screen>
          </IPadFrame>
        </MockupContainer>

        <Content>
          <h2>Never Miss a Payment with Smart Reminders</h2>
          <p>
            Stay on top of renewals with automated reminders for upcoming payments.
          </p>
          <p>
            Get notified in app and email before your subscriptions renew, helping you avoid
            unexpected charges and save time.
          </p>
        </Content>
      </Container>
    </Section>
  );
};

export default SmartReminders; 